/**
 * client/src/components/layout/handshake-push-listener.tsx
 *
 * Mounts the guardian-duty listener once, inside the authenticated app
 * shell, so a notice reaches a guardian on whatever page they happen to be
 * on (PR A, Task A3) — not only if they are already looking at Invitations.
 *
 * Mirrors `invitation-push-listener.tsx` exactly (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): two effects on arrival —
 *   1. Invalidate the pending-duties query, so the Invitations page's
 *      "Guardian approvals" section updates without a refresh.
 *   2. Raise a toast. Neutral/success-variant styling, not amber — a
 *      guardian being asked to approve something is not an error (CLAUDE.md
 *      Design Constraints reserve amber for user errors/dormancy).
 *
 * DEVIATION FROM PLAN, named honestly: as of #3169 the shared `toast()`
 * primitive (components/ui/toast.tsx) CAN carry an optional click-through
 * link (`toast.notice(message, { href, linkLabel })`), but that slot is not
 * used here — every other push listener in this codebase (including its own
 * mirror, `InvitationPushListener`) still raises a plain message, so no
 * "navigate on click" action is attached here either, rather than adding a
 * one-off link for one caller. The Invitations page's "Guardian approvals"
 * section (a plain authenticated read, reload-safe) is the reachable link to
 * `/guardian-stepup/:handshakeId`; this toast is a live heads-up layered on
 * top of it, exactly as `use-invitation-push.ts` states for its own rail —
 * reach is not guaranteed, so no copy claims delivery.
 *
 * Renders nothing. It is a listener, deliberately not a UI component.
 */

import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/ui/toast";
import { HANDSHAKE_DUTIES_QUERY_KEY } from "@/lib/handshake-duties-api";
import { describeHandshakeDuty, useHandshakePush } from "@/lib/use-handshake-push";

export function HandshakePushListener(): null {
  const queryClient = useQueryClient();

  useHandshakePush((frame) => {
    void queryClient.invalidateQueries({ queryKey: HANDSHAKE_DUTIES_QUERY_KEY });
    const message = describeHandshakeDuty(frame);
    // A missing/non-string operationType (malformed frame) skips the toast
    // rather than throwing — the duties list still refetches above.
    if (message) toast(message);
  });

  return null;
}
